import ProgressBar from "@/components/ProgressBar";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import albertProfessional from "@/assets/albert-saunders-professional.jpeg";
import puduLogo from "@/assets/pudu-logo.png";
import { 
  Award,
  CheckCircle2,
  Star,
  TrendingUp,
  Users,
  Building2,
  Rocket,
  Play
} from "lucide-react";

const About = () => {
  const timeline = [
    {
      year: "2019-2021",
      title: "Foundations Laid",
      description: "Albert launches his first service-based company and discovers firsthand how inefficient operations can crush growth. Begins learning automation, AI workflows, voice assistants, and CRM optimization."
    },
    {
      year: "2022-2024",
      title: "AI Becomes a Mission",
      description: "Builds custom AI sales assistants for local businesses. Develops signature automation systems that improve sales conversions by 30-60%."
    },
    {
      year: "2025",
      title: "Neil AI Global Solutions LLC Founded",
      description: "Opens headquarters in Greensboro, NC. Builds the Neil AI ecosystem: AI voice agents, marketing automation, smart scheduling, and CRM automation."
    },
    {
      year: "Late 2025",
      title: "Robotics Division Launched",
      description: "Partners with leading robotics manufacturers and begins offering service robots, cleaning robots, and delivery robots across multiple industries."
    },
    {
      year: "2026",
      title: "Expansion & Recognition",
      description: "Launches Neil AI Agent Mode Suite (Sales Pro, Ops Manager, Creative Director). Closes multiple high-value enterprise deals across North Carolina. Becomes a regional distributor recognized for top-tier service, training, and client support."
    },
    {
      year: "2027 & Beyond",
      title: "National Expansion",
      description: "National expansion. Launch proprietary full-stack AI suite with integrated robotics intelligence. Train and certify 10,000 entrepreneurs, business owners, and the next generation of creators in AI + robotics literacy."
    }
  ];

  const certifications = [
    {
      icon: Award,
      title: "Authorized PUDU Dealer",
      description: "Official partner for PUDU Robotics in the Southeast"
    },
    {
      icon: CheckCircle2,
      title: "Certified AI Integrator",
      description: "Specialized in CRM, voice, and robotics integration"
    },
    {
      icon: Building2,
      title: "Enterprise Solutions",
      description: "Trusted by Fortune 500 companies and growing businesses"
    }
  ];

  return (
    <>
      <SEO 
        title="About Neil AI Global Solutions | Our Story & Mission"
        description="Learn about Neil AI Global Solutions, your trusted partner in AI automation and PUDU Robotics. Discover our mission to transform businesses through cutting-edge technology."
        keywords="about neil ai, AI automation company, PUDU robotics dealer, business automation, technology solutions, authorized dealer"
      />
      <ProgressBar />
      <Navigation />
      
      <div 
        className="min-h-screen py-8 px-4 pt-40 sm:pt-44 md:pt-48"
        style={{
          background: `
            radial-gradient(1400px 900px at 15% 5%, hsl(169 100% 25% / 0.15) 0%, transparent 55%),
            radial-gradient(1100px 700px at 85% 75%, hsl(43 62% 40% / 0.12) 0%, transparent 55%),
            linear-gradient(180deg, hsl(210 83% 8%) 0%, hsl(210 83% 4%) 100%)
          `
        }}
      >
        <main 
          className="max-w-6xl mx-auto border border-[hsl(var(--line))] rounded-[16px] p-4 sm:p-6 md:p-12 mb-8 glass-strong"
          style={{
            boxShadow: "0 0 80px hsl(var(--primary) / 0.25), 0 8px 32px rgba(0, 0, 0, 0.6)"
          }}
        >
          {/* Hero Section */}
          <div className="text-center mb-12 md:mb-16">
            <Badge className="mb-4 text-xs sm:text-sm px-3 py-1">Founder-Led. Technology-Focused.</Badge>
            <h1 className="font-orbitron text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6 leading-tight">
              From Grit to <span className="text-primary">Global Vision</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed px-2">
              No Silicon Valley venture capital. No secret boardroom. No industry connections. Just faith, drive, and relentless focus.
            </p>
          </div>

          {/* Google Reviews */}
          <div className="mb-16 text-center">
            <Card className="glass-effect border-[hsl(var(--line))] inline-block px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-accent text-accent" />
                  ))}
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-foreground">4.9/5.0</div>
                  <div className="text-sm text-muted-foreground">Based on 87 Google Reviews</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Certifications & Badges */}
          <div className="mb-12 md:mb-16">
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 md:mb-8 leading-tight">
              Certified <span className="text-primary">Excellence</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {certifications.map((cert, index) => {
                const Icon = cert.icon;
                return (
                  <Card key={index} className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="font-bold text-lg mb-2 text-foreground">{cert.title}</h3>
                      <p className="text-sm text-muted-foreground">{cert.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* PUDU Logo */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground mb-3">Official Authorized Dealer</p>
              <img src={puduLogo} alt="PUDU Robotics" className="h-12 mx-auto opacity-80" />
            </div>
          </div>

          {/* Mission Section */}
          <div className="mb-12 md:mb-16">
            <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8 md:p-10">
              <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 leading-tight">
                Our <span className="text-primary">Mission</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground text-center max-w-4xl mx-auto leading-relaxed">
                To empower businesses with AI and robotics that make operations smarter, teams stronger, and service faster — without replacing people.
              </p>
              <p className="text-base sm:text-lg text-muted-foreground text-center max-w-4xl mx-auto leading-relaxed mt-4">
                We believe robots and AI are not here to take jobs. They're here to enhance human potential, reduce burnout, increase efficiency, and allow people to focus on higher-value work.
              </p>
            </Card>
          </div>

          {/* Company Timeline */}
          <div className="mb-12 md:mb-16">
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 leading-tight">
              Milestone Timeline — The Journey to <span className="text-primary">Innovation</span>
            </h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-primary via-accent to-primary opacity-30" />
              
              {/* Timeline items */}
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <Card className="glass-effect border-[hsl(var(--line))] p-6 hover:border-primary/40 transition-all">
                        <div className="text-primary font-bold text-2xl mb-2">{item.year}</div>
                        <h3 className="font-orbitron font-bold text-xl mb-2 text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground">{item.description}</p>
                      </Card>
                    </div>
                    
                    {/* Center dot */}
                    <div className="relative z-10">
                      <div className="w-4 h-4 rounded-full bg-primary shadow-[0_0_20px_rgba(0,255,200,0.6)]" />
                    </div>
                    
                    <div className="flex-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Founder Section */}
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-6 md:mb-8">
              <p className="text-xs md:text-sm font-bold tracking-[0.3em] text-primary mb-3">MEET THE FOUNDER</p>
              <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
                Built on Faith, Family & <span className="text-primary">Innovation</span>
              </h2>
            </div>
            <Card
              className="glass-effect border-[hsl(var(--line))] overflow-hidden relative"
              style={{ boxShadow: "0 0 60px hsl(43 62% 45% / 0.18), 0 8px 32px rgba(0,0,0,0.5)" }}
            >
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[hsl(43_62%_55%)] to-transparent" />
              <CardContent className="p-0">
                <div className="grid lg:grid-cols-[420px_1fr] gap-0">
                  <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 lg:aspect-auto lg:min-h-[520px]">
                    <img
                      src={albertProfessional}
                      alt="Albert O'Neil Saunders Jr. - Founder & CEO of Neil AI Global Solutions"
                      className="h-full w-full object-cover object-top"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent lg:hidden" />
                  </div>

                  <div className="p-6 sm:p-8 md:p-10">
                    <h3 className="font-orbitron text-2xl sm:text-3xl font-bold text-foreground mb-2">
                      Albert O'Neil Saunders Jr.
                    </h3>
                    <p className="text-primary font-bold text-sm sm:text-base mb-5 leading-relaxed">
                      Founder & CEO · AI Automation Visionary · Robotics Innovator · Business Transformation Leader
                    </p>

                    <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      <p>
                        Albert O'Neil Saunders Jr. is the Founder & CEO of <span className="text-foreground font-semibold">Neil AI Global Solutions</span> — a rapidly growing AI automation and robotics company focused on helping businesses modernize operations, increase efficiency, and scale into the future through intelligent technology.
                      </p>
                      <p>
                        Built on faith, family, leadership, and innovation, Albert's journey is far deeper than the typical tech-founder story. Before launching Neil AI, he spent over <span className="text-foreground font-semibold">11 years in the water purification industry</span> helping families gain access to cleaner, safer water while building strong relationships across North Carolina.
                      </p>
                      <p>
                        Through years of leadership in sales, operations, customer service, and business development, Albert saw firsthand how businesses struggled with labor shortages, employee burnout, operational inefficiencies, rising overhead costs, and inconsistent workflows. Rather than complain about the problem — he built a company designed to solve it.
                      </p>
                      <p>
                        Today Neil AI helps businesses transition from outdated manual operations into intelligent automated ecosystems through <span className="text-foreground font-semibold">AI Automation, Voice AI, Workflow Systems, Commercial Robotics, Operational Intelligence, and Business Transformation</span>.
                      </p>
                      <blockquote className="border-l-4 border-primary pl-4 sm:pl-6 italic text-foreground font-medium text-base sm:text-lg my-4">
                        "We don't replace people — we empower them."
                      </blockquote>
                      <p>
                        Beyond business, Albert is a devoted husband to Ashli Saunders and proud father of three. Neil AI is a family-built company driven by purpose, innovation, integrity, and long-term impact.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-6">
                      <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold">
                        <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                          Book a Consultation
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="border-primary/40 hover:bg-primary/10">
                        <Link to="/ai-automation">Explore Our Mission</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* COO Section */}
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-6 md:mb-8">
              <p className="text-xs md:text-sm font-bold tracking-[0.3em] text-primary mb-3">EXECUTIVE LEADERSHIP</p>
              <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
                Chief Operating <span className="text-primary">Officer</span>
              </h2>
            </div>
            <Card
              className="glass-effect border-[hsl(var(--line))] overflow-hidden relative"
              style={{ boxShadow: "0 0 60px hsl(195 100% 45% / 0.15), 0 8px 32px rgba(0,0,0,0.5)" }}
            >
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent" />
              <CardContent className="p-6 sm:p-8 md:p-10">
                <div className="grid md:grid-cols-[1fr_2fr] gap-6 md:gap-10 items-start">
                  <div>
                    <h3 className="font-orbitron text-2xl sm:text-3xl font-bold text-foreground mb-2">
                      Uche J. Obi
                    </h3>
                    <p className="text-primary font-bold text-sm sm:text-base mb-4">
                      Chief Operating Officer
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      12+ years of cross-functional leadership across financial services, automotive, commercial
                      operations, and logistics. Translates complex AI & robotics into clear, measurable business outcomes.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      "Operational Leadership",
                      "Deployment Management",
                      "Sales Infrastructure",
                      "Enterprise Scaling",
                      "Robotics Operations",
                      "AI Implementation Systems",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-2 p-3 rounded-lg border border-[hsl(var(--line))] bg-black/30 hover:border-primary/40 transition-colors"
                      >
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm text-foreground font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* What Makes Us Different */}
          <div className="mb-12 md:mb-16">
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 leading-tight">
              What Makes Neil AI <span className="text-primary">Different</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8">
                <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-primary mb-4">Founder-Led. Technology-Focused.</h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Representation matters. Innovation from diverse leadership matters. Neil AI stands as proof that the future of AI belongs to everyone.
                </p>
              </Card>
              
              <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8">
                <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-primary mb-4">💠 Hardware + Software Unified</h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3">
                  We are one of the only AI companies offering:
                </p>
                <ul className="text-sm sm:text-base text-muted-foreground space-y-1 list-disc list-inside">
                  <li>AI voice agents</li>
                  <li>CRM automation</li>
                  <li>Marketing automation</li>
                  <li>Website & app creation</li>
                  <li>Service robots</li>
                  <li>Cleaning robots</li>
                  <li>Delivery robots</li>
                </ul>
                <p className="text-sm sm:text-base text-muted-foreground mt-3 font-semibold">All under a single brand.</p>
              </Card>
              
              <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8">
                <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-primary mb-4">💠 Hands-On Support & Training</h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Weekly live trainings, 1-on-1 support, and hands-on onboarding make us a trusted partner — not just a vendor.
                </p>
              </Card>
              
              <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8">
                <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-primary mb-4">💠 ROI-First Approach</h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3">
                  Every solution we build is designed to:
                </p>
                <ul className="text-sm sm:text-base text-muted-foreground space-y-1">
                  <li>✅ Reduce operational costs</li>
                  <li>✅ Increase efficiency</li>
                  <li>✅ Improve customer satisfaction</li>
                  <li>✅ Scale without adding unnecessary labor</li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Vision for the Future */}
          <div className="mb-12 md:mb-16">
            <Card className="glass-effect border-[hsl(var(--line))] p-6 sm:p-8 md:p-10 bg-gradient-to-br from-primary/5 to-accent/5">
              <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 leading-tight">
                Our Vision for the <span className="text-primary">Future</span>
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-4xl mx-auto">
                <p>Neil AI is building a world where:</p>
                <ul className="space-y-3 ml-4">
                  <li>• A restaurant can run 24/7 with AI handling bookings, marketing, and service robots supporting staff.</li>
                  <li>• A hospital can improve patient care with autonomous cleaning robots and AI voice scheduling.</li>
                  <li>• Small businesses can compete like billion-dollar brands using AI tools engineered specifically for them.</li>
                </ul>
                <p className="pt-4 font-semibold text-foreground">
                  And along the way, Neil AI will continue breaking barriers, inspiring young Black innovators, and proving that the future is not just automated — it's diverse, inclusive, and powered by vision.
                </p>
              </div>
            </Card>
          </div>

          {/* Stats Section */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <Card className="glass-effect border-[hsl(var(--line))] p-6 text-center">
                <Rocket className="w-8 h-8 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-primary mb-1">12+</div>
                <div className="text-sm text-muted-foreground">Years Experience</div>
              </Card>
              <Card className="glass-effect border-[hsl(var(--line))] p-6 text-center">
                <Award className="w-8 h-8 text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-accent mb-1">4.9★</div>
                <div className="text-sm text-muted-foreground">Client Satisfaction</div>
              </Card>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center border-2 border-primary/30 rounded-2xl p-6 sm:p-8 md:p-10 bg-gradient-to-br from-primary/5 to-accent/5">
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
              Ready to <span className="text-primary">Automate</span> Your Future?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
              Join the automation revolution. Let's build something extraordinary together.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl px-10 py-7 text-lg shadow-[0_0_30px_rgba(0,255,200,0.5)] hover:shadow-[0_0_40px_rgba(0,255,200,0.7)] transition-all"
              >
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  📅 Schedule Your Demo
                </a>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 font-bold rounded-xl px-10 py-7 text-lg"
              >
                <Link to="/">
                  🏠 Explore Solutions
                </Link>
              </Button>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default About;
